import { request, type GetToken } from './client.js';
import type { BlockedEvent, RelaunchOutcome } from './types.js';

// Object events refused for their trigger's daily budget (retried every 15 minutes by the queue), and their relaunch.
// Kept apart from the main client: a relaunch answers its outcome in the status, so 429 and 409 are not errors here.

const API_URL = import.meta.env.VITE_API_URL || '';

export function createBlockedApi(getToken: GetToken) {
  return {
    list: (serverId: string) => request<BlockedEvent[]>(getToken, `/servers/${serverId}/blocked`),
    relaunch: async (serverId: string, externalRef: string): Promise<RelaunchOutcome> => {
      const token = await getToken();
      const response = await fetch(`${API_URL}/servers/${serverId}/blocked/${encodeURIComponent(externalRef)}/relaunch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const body = await response.json().catch(() => ({ message: response.statusText }));
      if (response.status === 429 && body.error === 'budget_exceeded') {
        return { outcome: 'over_budget', trigger: body.trigger, used: body.used, limit: body.limit, parameter: body.parameter };
      }
      if (response.status === 409) return { outcome: 'busy', message: body.message, retryAfterSeconds: body.retryAfterSeconds };
      if (!response.ok) throw new Error(body.message || `Request failed: ${response.status}`);
      if (body.status === 'started') return { outcome: 'running' };
      if (body.status === 'starting') return { outcome: 'starting' };
      return { outcome: 'handled', status: body.status, message: body.message };
    },
  };
}
